/**
 * <cart-recommendations>
 *
 * Renderiza, no carrinho, recomendações baseadas em TODOS os produtos que
 * estão no carrinho (não só um) — a API nativa da Shopify
 * (`/recommendations/products`) só aceita um `product_id` por vez, então
 * aqui a gente busca uma pra cada produto do carrinho, junta os
 * resultados (round-robin, sem repetir, sem indisponível e sem recomendar
 * o que já está no carrinho) e manda os handles escolhidos de volta pra
 * própria section via Section Rendering API (`?section_id=...&smart=1&handles=a,b,c`),
 * que resolve os handles, COMPLETA até `max_products` com a coleção de
 * fallback (pulando indisponíveis) e reconstrói os cards inteiros no
 * servidor — com toda a lógica de preço/swatch/etc. do
 * `_zrzs-product-card`, sem reimplementar nada disso em JS.
 *
 * Enquanto isso resolve, fica visível um skeleton (renderizado pelo
 * próprio servidor, `sections/cart-recommendations.liquid`). Se o fetch
 * falhar por qualquer motivo, revela o fallback que já está inline no
 * HTML (dentro de um `<template>`) em vez de deixar o skeleton preso.
 */

import {
  fetchAnchorRecommendations,
  mergeRoundRobin,
} from '@theme/cart-recommendations-core';

const MAX_ANCHOR_PRODUCTS = 6;
// Limite máximo aceito pela própria API de recomendações da Shopify.
const RECOMMENDATIONS_FETCH_LIMIT = 10;
// A lista final some coisas que o JS não tem como prever (ex.: produto
// com `hide_product_in_collection` marcado, só visível pro Liquid) — por
// isso o merge junta MAIS candidatos do que `maxProducts` (esse
// multiplicador), pra sobrar substituto smart em vez de cair na coleção
// de fallback à toa quando um candidato é rejeitado no servidor.
const CANDIDATE_POOL_MULTIPLIER = 3;

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    // Esta instância já É o resultado final (veio da Section Rendering
    // API com `?smart=1` resolvido no servidor) — nada a fazer.
    if (this.dataset.requestPerformed === 'true') return;

    const cartProductIds = (this.dataset.cartProductIds || '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);

    // Carrinho vazio: o servidor já renderizou o conteúdo final (fallback),
    // sem skeleton — nada pra buscar.
    if (cartProductIds.length === 0) return;

    this.#resolveSmartRecommendations(cartProductIds).catch(() => {
      this.#revealInlineFallback();
    });
  }

  /**
   * @param {string[]} cartProductIds
   */
  async #resolveSmartRecommendations(cartProductIds) {
    const recommendationsUrl = this.dataset.recommendationsUrl;
    const intent = this.dataset.intent || 'related';
    const maxProducts = Number(this.dataset.maxProducts) || 4;
    if (!recommendationsUrl) {
      this.#revealInlineFallback();
      return;
    }

    // Limita quantos produtos-âncora do carrinho a gente consulta, pra
    // não disparar fetches demais num carrinho muito grande.
    const anchors = cartProductIds.slice(0, MAX_ANCHOR_PRODUCTS);

    const perAnchorResults = await Promise.all(
      anchors.map(productId =>
        fetchAnchorRecommendations(
          productId,
          recommendationsUrl,
          intent,
          RECOMMENDATIONS_FETCH_LIMIT
        )
      )
    );

    // `poolSize` maior que `maxProducts`: o JS não sabe quais handles o
    // servidor vai rejeitar (ex. `hide_product_in_collection`, só visível
    // pro Liquid), então manda candidatos de sobra pra ele escolher os
    // primeiros válidos em vez de cair na coleção de fallback à toa.
    const candidatePoolSize = maxProducts * CANDIDATE_POOL_MULTIPLIER;
    const picked = mergeRoundRobin(
      perAnchorResults,
      cartProductIds,
      candidatePoolSize
    );
    const handles = picked.map(
      (/** @type {{ handle: string }} */ product) => product.handle
    );

    // Manda mesmo com a lista vazia (nenhuma recomendação encontrada) —
    // o servidor sabe completar 100% com a coleção de fallback.
    await this.#renderByHandles(handles);
  }

  /**
   * @param {string[]} handles
   */
  async #renderByHandles(handles) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.#revealInlineFallback();
      return;
    }

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('smart', '1');
    url.searchParams.set('handles', handles.join(','));

    const response = await fetch(url.toString());
    if (!response.ok) {
      this.#revealInlineFallback();
      return;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.getElementById(this.id);

    if (next && next.innerHTML.trim().length) {
      this.innerHTML = next.innerHTML;
      this.dataset.requestPerformed = 'true';
    } else {
      this.#revealInlineFallback();
    }
  }

  /**
   * Safety net: se a busca/section-render falhar por qualquer motivo,
   * revela o fallback que já está inline no HTML (sem precisar de mais
   * uma viagem de rede) em vez de deixar o skeleton preso pra sempre.
   */
  #revealInlineFallback() {
    const template = this.querySelector(
      'template[data-cart-recommendations-fallback]'
    );
    const skeleton = this.querySelector('[data-cart-recommendations-skeleton]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    template.replaceWith(fragment);
    skeleton?.remove();
    this.dataset.requestPerformed = 'true';
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
